import { PrivacyCenterPreferenceTopic } from '@main/cm-types';
import { CheckboxGroup, RadioGroup } from '@main/pc-core-components';
import { PreferenceChoiceSchema } from '@main/sombra-types';
import { PreferenceTopicType } from '@transcend-io/privacy-types';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

/**
 * Preferences list type as used in form
 */
export type Preference = Record<string, PreferenceChoiceSchema>;

interface PreferenceListProps {
  /** Purpose tracking type(slug) */
  purposeSlug: string;
  /** configured preference topics to show */
  preferences: PrivacyCenterPreferenceTopic[];
  /** existing preferences */
  value: Record<string, PreferenceChoiceSchema> | undefined;
  /** whether the preferences are disabled */
  disabled?: boolean;
  /** On change function */
  onChange?: (
    updatedPreferences: Record<string, PreferenceChoiceSchema>,
  ) => void;
}

/**
 *  Preferences list shown up on the Communications Preferences page
 */
export const PreferenceList: React.FC<
  React.PropsWithChildren<PreferenceListProps>
> = ({ purposeSlug, preferences, value, disabled = false, onChange }) => {
  const { formatMessage } = useIntl();
  const reshapedPreferenceTopics = useMemo(
    () =>
      preferences.map((preference) => ({
        ...preference,
        title: formatMessage(preference.title),
        displayDescription: formatMessage(preference.displayDescription),
        value: value?.[preference.slug] || null,
        preferenceOptionValues: preference.preferenceOptionValues.map(
          ({ slug, title }) => ({
            slug,
            title: formatMessage(title),
          }),
        ),
      })),
    [value, preferences, formatMessage],
  );

  return (
    <>
      {reshapedPreferenceTopics?.map((topic, index) => {
        if (topic.type === PreferenceTopicType.Select) {
          return (
            <RadioGroup
              key={index}
              name={`${purposeSlug}-${topic.slug}`}
              value={topic.value?.selectValue}
              title={topic.title}
              titleDescription={topic.displayDescription}
              disabled={disabled}
              options={
                topic.preferenceOptionValues.map(({ title, slug }) => ({
                  label: title,
                  value: slug,
                })) || []
              }
              onChange={(selectValue) => {
                if (onChange && selectValue) {
                  onChange({
                    ...value,
                    [topic.slug]: {
                      selectValue,
                    },
                  });
                }
              }}
            />
          );
        }
        if (topic.type === PreferenceTopicType.MultiSelect) {
          return (
            <CheckboxGroup
              key={index}
              name={`${purposeSlug}-${topic.slug}`}
              value={topic.value?.selectValues}
              title={topic.title}
              titleDescription={topic.displayDescription}
              disabled={disabled}
              options={
                topic.preferenceOptionValues.map(({ title, slug }) => ({
                  label: title,
                  value: slug,
                })) || []
              }
              onChange={(selectValues) => {
                if (onChange && selectValues) {
                  onChange({
                    ...value,
                    [topic.slug]: {
                      selectValues,
                    },
                  });
                }
              }}
            />
          );
        }
        if (topic.type === PreferenceTopicType.Boolean) {
          return (
            <CheckboxGroup
              key={index}
              name={`${purposeSlug}-${topic.slug}`}
              value={
                topic.value?.booleanValue
                  ? [`${topic.value?.booleanValue}`]
                  : undefined
              }
              disabled={disabled}
              title={topic.title}
              options={[
                {
                  label: topic.displayDescription,
                  value: 'true',
                },
              ]}
              onChange={(booleanValue) => {
                if (onChange && booleanValue) {
                  onChange({
                    ...value,
                    [topic.slug]: {
                      booleanValue:
                        booleanValue.length === 1 && booleanValue[0] === 'true',
                    },
                  });
                }
              }}
            />
          );
        }
        return null;
      })}
    </>
  );
};
